import React, { useEffect, useMemo, useState } from 'react';
import { CARD } from './styles';

/**
 * Product image gallery — main image, click-to-lightbox, thumbnail strip,
 * keyboard nav, body-scroll lock while open.
 *
 * Images are sorted: `is_primary` first, then by `sort_order`.
 */
export default function ProductGallery({ product, accent }) {
  const images = useMemo(() => {
    const arr = (product?.images || []).filter((i) => i && i.url);
    return arr.slice().sort((a, b) => {
      if (a.is_primary !== b.is_primary) return a.is_primary ? -1 : 1;
      return (a.sort_order || 0) - (b.sort_order || 0);
    });
  }, [product]);
  const [activeIdx, setActiveIdx] = useState(0);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  useEffect(() => { setActiveIdx(0); }, [product?._id]);

  // Keyboard nav when the lightbox is open: ← → to flip, Esc to close.
  useEffect(() => {
    if (!lightboxOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') setLightboxOpen(false);
      else if (e.key === 'ArrowRight') setActiveIdx((i) => (i + 1) % Math.max(1, images.length));
      else if (e.key === 'ArrowLeft')  setActiveIdx((i) => (i - 1 + Math.max(1, images.length)) % Math.max(1, images.length));
    };
    document.addEventListener('keydown', onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightboxOpen, images.length]);

  const active = images[activeIdx];

  if (images.length === 0) {
    return (
      <div style={{ ...CARD, padding: 0, overflow: 'hidden' }}>
        <div style={{
          aspectRatio: '1 / 1', background: '#f1f5f9',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          color: '#94a3b8',
        }}>
          No image
        </div>
      </div>
    );
  }

  return (
    <div>
      <div
        style={{ ...CARD, padding: 0, overflow: 'hidden', marginBottom: 10, cursor: 'zoom-in' }}
        onClick={() => setLightboxOpen(true)}
        role='button'
        aria-label='Open larger image'
      >
        <div style={{ aspectRatio: '1 / 1', background: '#f1f5f9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <img
            src={active.url}
            alt={active.alt || product.name}
            style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          />
        </div>
      </div>

      {lightboxOpen && (
        <div
          onClick={() => setLightboxOpen(false)}
          style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15, 23, 42, 0.92)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 100, cursor: 'zoom-out',
          }}
          role='dialog'
          aria-modal='true'
        >
          <button
            type='button'
            onClick={(e) => { e.stopPropagation(); setLightboxOpen(false); }}
            aria-label='Close'
            style={{
              position: 'absolute', top: 18, right: 20,
              background: 'transparent', color: '#fff',
              border: 'none', fontSize: 28, cursor: 'pointer',
              lineHeight: 1, padding: 4,
            }}
          >×</button>
          <img
            src={active.url}
            alt={active.alt || product.name}
            onClick={(e) => e.stopPropagation()}
            style={{
              maxWidth: 'min(96vw, 1400px)',
              maxHeight: '92vh',
              objectFit: 'contain',
              boxShadow: '0 30px 80px rgba(0,0,0,0.4)',
            }}
          />
          {images.length > 1 && (
            <>
              <button
                type='button'
                onClick={(e) => { e.stopPropagation(); setActiveIdx((i) => (i - 1 + images.length) % images.length); }}
                aria-label='Previous image'
                style={{
                  position: 'absolute', left: 16, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.1)', color: '#fff',
                  border: 'none', borderRadius: '50%',
                  width: 44, height: 44, fontSize: 22, cursor: 'pointer',
                }}
              >‹</button>
              <button
                type='button'
                onClick={(e) => { e.stopPropagation(); setActiveIdx((i) => (i + 1) % images.length); }}
                aria-label='Next image'
                style={{
                  position: 'absolute', right: 16, top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'rgba(255,255,255,0.1)', color: '#fff',
                  border: 'none', borderRadius: '50%',
                  width: 44, height: 44, fontSize: 22, cursor: 'pointer',
                }}
              >›</button>
              <div style={{
                position: 'absolute', bottom: 18, left: 0, right: 0,
                textAlign: 'center', color: '#cbd5e1', fontSize: 12,
              }}>
                {activeIdx + 1} / {images.length}
              </div>
            </>
          )}
        </div>
      )}
      {images.length > 1 && (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(64px, 1fr))',
          gap: 8,
        }}>
          {images.map((img, idx) => {
            const isActive = idx === activeIdx;
            return (
              <button
                key={`${img.url}-${idx}`}
                type='button'
                onClick={() => setActiveIdx(idx)}
                aria-label={`Show image ${idx + 1}`}
                style={{
                  padding: 0,
                  background: '#f1f5f9',
                  border: `2px solid ${isActive ? accent : 'transparent'}`,
                  borderRadius: 8,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  aspectRatio: '1 / 1',
                  opacity: isActive ? 1 : 0.85,
                  transition: 'opacity 120ms ease, border-color 120ms ease',
                }}
              >
                <img
                  src={img.url}
                  alt={img.alt || ''}
                  style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                />
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
